/**
 * Route table for the AVOS Application_Shell.
 *
 * A single, ordered source of truth for the shell's primary navigation and the
 * route-to-screen mapping. Both the server render path and the client hydration
 * path read this table so the server-rendered navigation and the hydrated
 * navigation stay identical (Req 21.1: layout + navigation present in the
 * initial response body).
 *
 * Later web tasks (the thin screen slice — registration, login, MFA, role
 * management, impersonation consent, export, deletion — Req 22-25) plug in by
 * adding an entry here and supplying the corresponding lazy screen component in
 * `screens.tsx`. Nothing else in the shell needs to change: navigation,
 * matching, and code splitting are all driven off this table.
 */

/** A single navigable route in the shell. */
export interface ShellRoute {
  /** URL path this route matches (exact match against the request pathname). */
  readonly path: string;
  /** Human-readable label rendered in the primary navigation. */
  readonly label: string;
  /**
   * Key identifying which lazy screen component renders for this route. The
   * screen registry (`screens.tsx`) maps this key to a `React.lazy` component
   * so route components are code-split per route (Req: route-level code
   * splitting).
   */
  readonly screen: ScreenKey;
}

/**
 * Stable identifiers for the M0 screens. Kept as a string-literal union so the
 * route table and the screen registry cannot drift: adding a route with an
 * unknown screen key is a compile error.
 */
export type ScreenKey =
  | "home"
  | "signIn"
  | "account"
  | "login"
  | "mfaChallenge"
  | "mfaEnrollment"
  | "register"
  | "verifyEmail"
  | "roleManagement"
  | "impersonationConsent"
  | "dataExport"
  | "accountDeletion";

/**
 * The standardised PUBLIC authentication routes, in display order. These are
 * the only routes the unauthenticated shell serves directly:
 *   /login · /signup · /verify-email · /mfa
 * (Authenticated account functions now live under /app/settings/* inside the
 * app shell.) The public/auth screens no longer render the old foundation
 * horizontal navigation, so this table drives route→screen resolution and the
 * entry redirect, not a visible nav bar.
 */
export const SHELL_ROUTES: readonly ShellRoute[] = [
  { path: "/login", label: "Log in", screen: "login" },
  { path: "/signup", label: "Sign up", screen: "register" },
  { path: "/verify-email", label: "Verify email", screen: "verifyEmail" },
  { path: "/mfa", label: "Verify code", screen: "mfaChallenge" },
] as const;

/**
 * Back-compat + still-reachable screens that are NOT part of the primary public
 * auth surface: the old marketing/sign-in/account routes and the MFA-enrolment
 * screen. Kept resolvable so deep links and in-app flows continue to work while
 * the account functions conceptually move under /app/settings/*.
 */
const LEGACY_ROUTES: readonly ShellRoute[] = [
  { path: "/", label: "Home", screen: "home" },
  { path: "/sign-in", label: "Sign in", screen: "signIn" },
  { path: "/account", label: "Account", screen: "account" },
  { path: "/mfa-enroll", label: "Set up 2FA", screen: "mfaEnrollment" },
  { path: "/register", label: "Register", screen: "register" },
  { path: "/account/roles", label: "Team roles", screen: "roleManagement" },
  {
    path: "/account/support-access",
    label: "Support access",
    screen: "impersonationConsent",
  },
  { path: "/account/export", label: "Export data", screen: "dataExport" },
  { path: "/account/delete", label: "Delete account", screen: "accountDeletion" },
] as const;

/** Every resolvable route (public auth + back-compat), for route→screen lookup. */
export const ALL_SHELL_ROUTES: readonly ShellRoute[] = [
  ...SHELL_ROUTES,
  ...LEGACY_ROUTES,
];

/**
 * Resolve a request pathname to a route. Falls back to the login route when no
 * route matches so the unauthenticated shell always renders a valid screen and
 * never fails to produce a body.
 */
export function matchRoute(pathname: string): ShellRoute {
  const normalized = normalizePathname(pathname);
  const match = ALL_SHELL_ROUTES.find((route) => route.path === normalized);
  return match ?? (SHELL_ROUTES[0] as ShellRoute);
}

/**
 * Normalize a pathname for matching: strip any query/hash and trailing slash
 * (except the root). Keeps matching deterministic across server and client.
 */
function normalizePathname(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/)[0] ?? "/";
  if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
    return withoutQuery.slice(0, -1);
  }
  return withoutQuery === "" ? "/" : withoutQuery;
}
